import CLIENT from '../utils/restClient';
import FileSaver from 'file-saver';
import Logger, { LOG_LEVELS } from '../utils/logger';
import { FrequencyMap, VatTypeMap } from '../utils/enums';

// Custom filename generator for CSV exports and workflow
export function generateCustomCsvFilename(params) {
  const {
    transactionTypeName,
    countryCode,
    frequency,
    startDate,
    companyName,
    companyCode,
    returnDisplayType
  } = params;

  // Format: <VATType>_<Country>_<Frequency>_<Period>_<CompanyCode>-<CompanyName>_<type>_Reconciliation.csv
  // Example: VAT_Regular_NL_Quarterly_202506_123-Valentino_SPA_Sales_Reconciliation.csv

  // 1. VAT Type - determine from returnDisplayType
  const vatType = VatTypeMap[returnDisplayType] || '';

  // 2. Country - use the country code
  const country = countryCode || '';

  // 3. Frequency - map frequency to readable format
  const frequencyText = FrequencyMap[frequency] || '';

  // 4. Period - format dates to YYYYMM
  const period = startDate ? startDate.substring(0, 7).replace('-', '') : '';

  // 5. Client - use company code and company name
  const client = companyCode && companyName ? `${companyCode}-${companyName}` : (companyName || companyCode || '');

  // 6. Type - capitalize first character of transaction type
  const type = transactionTypeName ? transactionTypeName.charAt(0).toUpperCase() + transactionTypeName.slice(1) : '';

  // 7. Add reconciliation suffix
  const filename = `VAT_${vatType}_${country}_${frequencyText}_${period}_${client}_${type}_Reconciliation.csv`;

  return filename;
}

export const MODULE_NAME = 'VAT_RETURN_BY_TRANSACTIONS_CONTROLLER';
const log = new Logger(MODULE_NAME, LOG_LEVELS.DEBUG);

function namespaced(name) {
  return MODULE_NAME + '.' + name;
}
const DOWNLOAD_CSV_EXPORT = namespaced('DOWNLOAD_CSV_EXPORT');
const DOWNLOAD_CSV_EXPORT_OK = namespaced('DOWNLOAD_CSV_EXPORT_OK');
const DOWNLOAD_CSV_EXPORT_FAIL = namespaced('DOWNLOAD_CSV_EXPORT_FAIL');
const LOAD = namespaced('LOAD');
const LOAD_OK = namespaced('LOAD_OK');
const LOAD_FAIL = namespaced('LOAD_FAIL');

export const ACTION_TYPES = {
  DOWNLOAD_CSV_EXPORT,
  DOWNLOAD_CSV_EXPORT_OK,
  DOWNLOAD_CSV_EXPORT_FAIL,
  LOAD,
  LOAD_OK,
  LOAD_FAIL
};

export const updateSubStateFromAction = (state = {data: undefined, loading: false, error: null}, action) => {
  log.debug('updateSubStateFromAction: ', action, state );
  const getNewState = (action, state) => {
    switch (action.type) {
      case LOAD:
        return Object.assign({}, state, {loading: true, error: null});
      case LOAD_OK:
        return Object.assign({}, state, {data: action.response, loading: false, error: null, progress: undefined});
      case LOAD_FAIL:
        return Object.assign({}, state, {data: undefined, loading: false, error: action.error});
      case DOWNLOAD_CSV_EXPORT:
        return Object.assign({}, state, {loading: true, error: null});
      case DOWNLOAD_CSV_EXPORT_OK:
        return Object.assign({}, state, {loading: false, error: null, progress: undefined});
      case DOWNLOAD_CSV_EXPORT_FAIL:
        return Object.assign({}, state, {loading: false, error: action.error});
    default:
        return state;
    }
  };
  const newState = getNewState(action, state);
  log.debug('updateSubStateFromAction:  newState', newState );
  return newState;
};
export const API = {
  doLoad: (params) => {
    return (dispatch, getState) => {
      dispatch({type: LOAD});
      log.debug('doLoad(): params: ', params);

      // api/filing/types/vat/403/transactions/AT/2019-05-01/2019-05-31/1/0/1/100
      CLIENT.getFilingTransactions(params)
        .then((response) => {
          log.debug('doLoad(): succeeded: ', response);
          return response
        })
        .then((response) => {
          log.debug('Restructure response to have flattened boxes ', response);

          let restructuredResponse = {};
          restructuredResponse = response;

          // Make a new element on each transaction for the dynamic column definition
          response.transactions.map((transaction, id) => {
            let transactionFlatBoxes = [];
            transaction.boxes.forEach(box =>
              transactionFlatBoxes[box.name] = Number(box.boxValue).toFixed(2));
            return restructuredResponse.transactions[id].flatboxes = transactionFlatBoxes;
          });

          // Make a new element for the totals to be used in the footer
          let totalsFlatBoxes = [];
          response.totals.boxes.forEach(box => 
            totalsFlatBoxes[box.name] = Number(box.boxValue).toFixed(2));
          restructuredResponse.totals.flatBoxes = totalsFlatBoxes;

          // Return with a modified response object
          dispatch({type: LOAD_OK, response: restructuredResponse});
        })
        .catch((error) => {
          log.debug('doLoad(): failed: ', error)
          dispatch({type: LOAD_FAIL, error});
        });
    }
  },
  doDownloadTransactionsCsv: (params) => {
    return async (dispatch) => {
      try {
        dispatch({ type: ACTION_TYPES.DOWNLOAD_CSV_EXPORT });
        log.debug('doDownloadTransactionsCsv(): params: ', params);

        const file = await CLIENT.downloadFilingTransactionsCsv(params);
        const fileName = generateCustomCsvFilename(params);

        try {
          FileSaver.saveAs(file, fileName);
        }
        catch (error) {
          log.debug('doDownloadTransactionsCsv(): error saving file to filesystem');
          dispatch({ type: DOWNLOAD_CSV_EXPORT_FAIL, error });
        }

        dispatch({ type: DOWNLOAD_CSV_EXPORT_OK, reponse: file.blob });
      } catch (error) {
        log.debug('doDownloadTransactionsCsv(): error', error);
        dispatch({ type: ACTION_TYPES.DOWNLOAD_CSV_EXPORT_FAIL, error });
      }
    };
  },
  doSaveToWorkflow: (params, callback) => {
    return async () => {
      try {
        log.debug('doSaveToWorkflow(): params: ', params);

        const res = await CLIENT.exportReportToWorkflow(params);
        callback(null, res);
      } catch (error) {
        log.debug('doSaveToWorkflow(): error', error);
        callback(error, null);
      }
    };
  }
};

export default API;
